/**
 * PendingMatchBanner
 * Alert banner shown at the top of lease detail (all tabs) when one or more pending suggested
 * matches exist. Opens COLLAPSED — the newest match with its one-tap confirm/dismiss (US-56,
 * unchanged single-match behavior) plus a "(+N more)" count — and expands on demand to list
 * EVERY pending match, each with its own working confirm/dismiss (US-66, AC-12/AC-13).
 */

import { useState } from 'react';
import { AlertCircle, Check, ChevronDown, X } from 'lucide-react';
import { Button } from '../ui/button';
import type { LeaseStatusPendingMatch } from '@resida/api-types/payments';
import { formatCalendarDate } from '../../utils/date';
import { asCalendarDateString } from '@resida/shared';

/**
 * The boundary contract itself (AC-14) — not a local re-declaration. This component reads only a
 * subset of the fields, but typing against the contract means a change to the endpoint's shape
 * surfaces here at typecheck rather than silently diverging.
 */
type PendingMatch = LeaseStatusPendingMatch;

interface PendingMatchBannerProps {
  matches: PendingMatch[];
  /** True uncapped total of pending suggested matches on this lease (may exceed matches.length). */
  totalCount: number;
  onConfirm: (transactionId: string) => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void> | void;
}

export function PendingMatchBanner({
  matches,
  totalCount,
  onConfirm,
  onDismiss,
}: PendingMatchBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const newest = matches[0];
  if (!newest) return null;
  const additionalCount = Math.max(totalCount - 1, 0);

  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3"
      data-testid="pending-match-banner"
    >
      {!isExpanded ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="flex items-start sm:items-center gap-2 min-w-0">
            <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5 sm:mt-0" aria-hidden="true" />
            <p className="text-sm text-foreground">
              External payment of{' '}
              <span className="font-medium">{formatAmount(newest.amount)}</span>{' '}
              detected on {formatCalendarDate(asCalendarDateString(newest.date), { month: 'short', day: 'numeric' })} from{' '}
              <span className="font-medium">{newest.description}</span>
              {additionalCount > 0 && (
                <span className="text-warning"> (+{additionalCount} more)</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 pl-6 sm:pl-0">
            {additionalCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="min-h-[44px] text-warning hover:bg-warning/10"
                onClick={() => setIsExpanded(true)}
                data-testid="pending-match-expand"
                aria-label="Show all pending matches"
                aria-expanded={false}
              >
                <ChevronDown className="h-3 w-3 mr-1" />
                Show all
              </Button>
            )}
            <MatchRowActions
              transactionId={newest.transactionId}
              onConfirm={onConfirm}
              onDismiss={onDismiss}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertCircle className="h-4 w-4 text-warning shrink-0" aria-hidden="true" />
              <p className="text-sm font-medium text-foreground m-0">
                {totalCount} pending match{totalCount === 1 ? '' : 'es'}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="min-h-[44px] text-warning hover:bg-warning/10"
              onClick={() => setIsExpanded(false)}
              data-testid="pending-match-collapse"
              aria-label="Collapse pending matches"
              aria-expanded={true}
            >
              <ChevronDown className="h-3 w-3 mr-1 rotate-180" />
              Collapse
            </Button>
          </div>
          <div className="flex flex-col gap-2">
            {matches.map((match) => (
              <PendingMatchRow key={match.transactionId} match={match} onConfirm={onConfirm} onDismiss={onDismiss} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** One expanded-list row: its own amount/date/description plus its own working confirm/dismiss,
 * with in-flight/error state tracked PER ROW so one row's pending action never disables another. */
function PendingMatchRow({
  match,
  onConfirm,
  onDismiss,
}: {
  match: PendingMatch;
  onConfirm: (transactionId: string) => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void> | void;
}) {
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 rounded-md border border-warning/20 bg-background/50 px-3 py-2"
      data-testid={`pending-match-row-${match.transactionId}`}
    >
      <p className="text-sm text-foreground min-w-0">
        <span className="font-medium">{formatAmount(match.amount)}</span>{' '}
        on {formatCalendarDate(asCalendarDateString(match.date), { month: 'short', day: 'numeric' })} from{' '}
        <span className="font-medium">{match.description}</span>
        {match.tenantName && <span className="text-muted-foreground"> · {match.tenantName}</span>}
      </p>
      <div className="shrink-0">
        <MatchRowActions transactionId={match.transactionId} onConfirm={onConfirm} onDismiss={onDismiss} />
      </div>
    </div>
  );
}

/** Confirm/Dismiss pair for a single match, with its own isolated pending/error state. */
function MatchRowActions({
  transactionId,
  onConfirm,
  onDismiss,
}: {
  transactionId: string;
  onConfirm: (transactionId: string) => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void> | void;
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const isActionPending = isConfirming || isDismissing;

  const handleConfirm = async () => {
    setIsConfirming(true);
    setActionError(null);
    try {
      await onConfirm(transactionId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to confirm match');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDismiss = async () => {
    setIsDismissing(true);
    setActionError(null);
    try {
      await onDismiss(transactionId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to dismiss match');
    } finally {
      setIsDismissing(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 items-end">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="min-h-[44px] border-warning/30 text-warning hover:bg-warning/10"
          onClick={handleDismiss}
          disabled={isActionPending}
          data-testid={`pending-match-dismiss-${transactionId}`}
          aria-label="Dismiss match"
        >
          <X className="h-3 w-3 mr-1" />
          {isDismissing ? 'Dismissing...' : 'Dismiss'}
        </Button>
        <Button
          size="sm"
          className="min-h-[44px] bg-warning hover:bg-warning/90 text-warning-foreground"
          onClick={handleConfirm}
          disabled={isActionPending}
          data-testid={`pending-match-confirm-${transactionId}`}
          aria-label="Confirm match"
        >
          <Check className="h-3 w-3 mr-1" />
          {isConfirming ? 'Confirming...' : 'Confirm'}
        </Button>
      </div>
      {actionError && (
        <p className="text-xs text-destructive" data-testid="pending-match-banner-error">
          {actionError}
        </p>
      )}
    </div>
  );
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount));
}
